import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = "Hi! I'm Casey's Cadence assistant. Tell me about your team or event and I'll help you find the right rhythm-based keynote or workshop.";

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    let sid = localStorage.getItem("cc_chat_sid");
    if (!sid) {
      sid = "sid-" + (crypto.randomUUID ? crypto.randomUUID() : Date.now() + "-" + Math.random().toString(36).slice(2));
      localStorage.setItem("cc_chat_sid", sid);
    }
    sessionId.current = sid;
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — I'm having trouble right now. Please use the booking form and Casey's team will reach out." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Open chat"}
        data-testid="chat-toggle"
        data-cursor="org"
        className="fixed bottom-4 left-4 z-[80] flex h-14 w-14 items-center justify-center rounded-full bg-[#D4AF37] text-[#0A2E2C] shadow-2xl transition-transform hover:scale-105 md:bottom-8 md:left-8"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {/* panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-20 left-4 z-[80] flex h-[70vh] max-h-[560px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-[#AEE6D5]/20 bg-[#0A2E2C] shadow-2xl md:bottom-28 md:left-8"
        >
          <div className="border-b border-[#AEE6D5]/12 bg-[#0B3B3C] px-5 py-4">
            <p className="font-mono text-[0.58rem] uppercase tracking-[0.24em] text-[#D4AF37]">Casey&#39;s Cadence</p>
            <p className="font-serif text-lg text-[#F8FAFC]">Chat with us</p>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid="chat-message"
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-[#D4AF37] text-[#0A2E2C]"
                    : "bg-[#0B3B3C] text-[#F8FAFC]/90"
                }`}
              >
                {m.content || <Loader2 className="h-4 w-4 animate-spin text-[#AEE6D5]" />}
              </div>
            ))}
          </div>

          <div className="border-t border-[#AEE6D5]/12 p-3">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Ask about keynotes, workshops, rates…"
                data-testid="chat-input"
                className="max-h-24 flex-1 resize-none rounded-xl border border-[#AEE6D5]/25 bg-[#0B3B3C]/60 px-3 py-2.5 text-sm text-[#F8FAFC] placeholder-[#F8FAFC]/40 outline-none transition-colors focus:border-[#D4AF37]"
              />
              <button
                onClick={send}
                disabled={busy || !input.trim()}
                data-testid="chat-send"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#D4AF37] text-[#0A2E2C] transition-colors hover:bg-[#B99628] disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
